import { useStore } from '@nanostores/react'
import { Dialog as DialogPrimitives } from 'radix-ui'
import { GradientBorderBox } from '~/components/gradient-border'
import { Spinner } from '~/components/ui/spinner'
import { $dialog, dialog } from '~/store/dialog'

export function Dialog() {
  const dialogState = useStore($dialog)

  const handleButtonClick = () => {
    if (dialogState.dialog.asyncAction) {
      dialogState.dialog.asyncAction()
    } else {
      dialogState.dialog.action?.()
      dialog.close()
    }
  }

  return (
    <DialogPrimitives.Root
      open={dialogState.isOpen}
      onOpenChange={(nextIsOpen) => {
        if (!nextIsOpen && !dialogState.isLoading) dialog.close()
      }}
    >
      <DialogPrimitives.Portal>
        <DialogPrimitives.Overlay
          className="fixed inset-0 z-[55] bg-black/10 backdrop-blur-[12.5px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          style={{
            pointerEvents: 'none',
          }}
        />
        <DialogPrimitives.Content
          className="fixed left-1/2 top-1/2 z-[60] w-[calc(100vw-48px)] max-w-[345px] -translate-x-1/2 -translate-y-1/2 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          onPointerDownOutside={(event) => event.preventDefault()}
          onInteractOutside={(event) => event.preventDefault()}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.preventDefault()}
          style={{
            ['--tw-enter-translate-x' as any]: '-50%',
            ['--tw-enter-translate-y' as any]: '-50%',
            ['--tw-exit-translate-x' as any]: '-50%',
            ['--tw-exit-translate-y' as any]: '-50%',
            pointerEvents: 'auto',
          }}
        >
          <GradientBorderBox
            className="bg-white/70 backdrop-blur-[50px]"
            radius={32}
            borderWidth={2}
            style={{
              willChange: 'backdrop-filter',
            }}
          >
            <div className="p-6 text-center">
              <DialogPrimitives.DialogTitle className="text-lg font-semibold text-[#333]">
                {dialogState.dialog.title}
              </DialogPrimitives.DialogTitle>

              <DialogPrimitives.DialogDescription className="mt-3 whitespace-pre-line text-[#666]">
                {dialogState.dialog.description}
              </DialogPrimitives.DialogDescription>

              {dialogState.dialog.asyncAction ? (
                <button
                  className="relative mt-4 flex h-[52px] w-full items-center justify-center rounded-xl bg-[#176361] font-bold text-white disabled:bg-[#639897]"
                  onClick={handleButtonClick}
                  disabled={dialogState.isLoading}
                >
                  {dialogState.isLoading && (
                    <Spinner className="absolute right-7 text-[#176361]" />
                  )}
                  {dialogState.dialog.button}
                </button>
              ) : (
                <DialogPrimitives.Close asChild>
                  <button
                    className="mt-4 h-[52px] w-full rounded-xl bg-[#176361] font-bold text-white"
                    {...(dialogState.dialog.action && {
                      onClick: () => dialogState.dialog.action!(),
                    })}
                  >
                    {dialogState.dialog.button}
                  </button>
                </DialogPrimitives.Close>
              )}
            </div>
          </GradientBorderBox>

          {dialogState.dialog.closeButton && !dialogState.isLoading && (
            <DialogPrimitives.Close
              className="absolute right-[-10px] top-[-10px] size-[42px]"
              onClick={() => {
                if (typeof dialogState.dialog.closeButton === 'object') {
                  dialogState.dialog.closeButton.action()
                }
              }}
            >
              <CloseSvg />
            </DialogPrimitives.Close>
          )}
        </DialogPrimitives.Content>
      </DialogPrimitives.Portal>
    </DialogPrimitives.Root>
  )
}

function CloseSvg() {
  return (
    <svg
      width={42}
      height={42}
      viewBox="0 0 42 42"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="pointer-events-none"
    >
      <foreignObject x={-87.5} y={-87.5} width={217} height={217}>
        <div
          style={{
            backdropFilter: 'blur(43.75px)',
            clipPath: 'url(#bgblur_0_1603_305_clip_path)',
            height: '100%',
            width: '100%',
          }}
        />
      </foreignObject>
      <g data-figma-bg-blur-radius={87.5}>
        <rect
          x={0.875}
          y={0.875}
          width={40.25}
          height={40.25}
          rx={20.125}
          fill="white"
          fillOpacity={0.56}
        />
        <rect
          x={0.875}
          y={0.875}
          width={40.25}
          height={40.25}
          rx={20.125}
          fill="#2C2C2C"
          style={{
            mixBlendMode: 'color-dodge',
          }}
        />
        <rect
          x={0.875}
          y={0.875}
          width={40.25}
          height={40.25}
          rx={20.125}
          stroke="url(#paint0_linear_1603_305)"
          strokeWidth={1.75}
        />
        <path
          d="M16 16L25.9999 25.9999"
          stroke="#333333"
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M16.0001 25.9999L26 16"
          stroke="#333333"
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath
          id="bgblur_0_1603_305_clip_path"
          transform="translate(87.5 87.5)"
        >
          <rect x={0.875} y={0.875} width={40.25} height={40.25} rx={20.125} />
        </clipPath>
        <linearGradient
          id="paint0_linear_1603_305"
          x1={22.4882}
          y1={-7.88143e-7}
          x2={34.3401}
          y2={38.3359}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" stopOpacity={0.3} />
          <stop offset={1} stopColor="white" stopOpacity={0.1} />
        </linearGradient>
      </defs>
    </svg>
  )
}
